import { supabase } from '@/services/supabase';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export type PermissionResult = 'granted' | 'denied' | 'default';

// Convert a URL-safe base64 string to a Uint8Array for use as applicationServerKey.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

/** Request browser notification permission. Returns the resulting permission state. */
export async function requestPermission(): Promise<PermissionResult> {
  if (!('Notification' in window)) return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}

/** Get the current push subscription for this browser, if any. */
export async function getSubscription(): Promise<PushSubscription | null> {
  if (!navigator.serviceWorker) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/** Subscribe this browser to Web Push. Returns null if not supported or VAPID key missing. */
export async function subscribe(): Promise<PushSubscription | null> {
  if (!navigator.serviceWorker || !('PushManager' in window)) return null;
  if (!VAPID_PUBLIC_KEY) {
    console.error('[PushSubscription] VITE_VAPID_PUBLIC_KEY is not configured');
    return null;
  }
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  });
}

/** Unsubscribe this browser from Web Push. Returns true on success. */
export async function unsubscribe(): Promise<boolean> {
  const subscription = await getSubscription();
  if (!subscription) return true;
  return subscription.unsubscribe();
}

/** Persist a PushSubscription to the push_subscriptions Supabase table. */
export async function saveSubscriptionToSupabase(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      endpoint: subscription.endpoint,
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'endpoint' },
  );
  if (error) {
    console.error('[PushSubscription] Failed to save subscription:', error.message);
  }
}

/** Delete this browser's push subscription from Supabase. */
export async function removeSubscriptionFromSupabase(endpoint: string): Promise<void> {
  const { error } = await supabase
    .from('push_subscriptions')
    .delete()
    .eq('endpoint', endpoint);
  if (error) {
    console.error('[PushSubscription] Failed to remove subscription:', error.message);
  }
}

/** Count how many active push subscriptions the current user has (one per device/browser). */
export async function getSubscriptionCount(): Promise<number> {
  const { count, error } = await supabase
    .from('push_subscriptions')
    .select('*', { count: 'exact', head: true });
  if (error) return 0;
  return count ?? 0;
}
